import state, {
  deleteNotification,
  getNotificationsFlyout,
  markNotification,
} from "@stores/notifications.store";
import { Component, Element, h, Host, JSX, Listen, Prop } from "@stencil/core";

// this component replaces deprecated bcp-notifications-flyout

@Component({
  tag: "f-notification-flyout",
  shadow: true,
})
export class NotificationFlyout {
  @Element() el: HTMLFNotificationFlyoutElement;

  /**
   * Hidden label of the notification flyout button
   */
  @Prop() buttonHiddenLabel = "Notifications";
  /**
   * title of the notification flyout button
   */
  @Prop() buttonTitle = this.buttonHiddenLabel;
  /**
   * close button text in flyout
   */
  @Prop() closeButtonHiddenLabel;
  /**
   * expander text in flyout
   */
  @Prop() expanderButtonHiddenLabel;
  /**
   * Empty flyout message
   */
  @Prop() emptyFlyoutMessage = "No new notifications";
  /**
   * Flyout header
   */
  @Prop() header = "My notifications";
  /**
   * Notifications page link label
   */
  @Prop() notificationsPageLinkName = "Complete list of notifications";
  /**
   * Notifications page link url
   */
  @Prop() notificationsPageLink = "#";
  /**
   * Notifications settings link label
   */
  @Prop() notificationsSettingsLinkName = "Settings";
  /**
   * Notifications settings link label
   */
  @Prop() notificationsSettingsLabel = "Settings";
  /**
   * Notifications settings link url
   */
  @Prop() notificationsSettingsLink = "#";
  /**
   * Remove notification label
   */
  @Prop() removeNotificationLabel = (window as any).translations["bcp-acc"]
    .remove;
  /**
   * Delete action label
   */
  @Prop() deleteAllLabel = "Delete all";
  /**
   * Mark as unread label
   */
  @Prop() unreadLabel = "Mark as unread";
  /**
   * Mark as read label
   */
  @Prop() readLabel = "Mark as read";
  /**
   * Mark all as read label
   */
  @Prop() markAllLabel = "Mark all as read";
  /**
   * Tooltip text for "Mark all as read" button
   */
  @Prop() markAllTooltip =
    "All your notifications will be marked as read. Action can only be reverted individually per notification";
  /**
   * Displayed time format
   */
  @Prop() timeFormat = "dd-LLL-yyyy, hh:mm a";

  /** Specifies whether the flyout is open */
  @Prop({ mutable: true }) open = false;

  /** Specifies the starting number of items (unread notifications) on the flyout list. */
  @Prop({ mutable: true }) badgeCounter: number;

  /** Defines the number of initially shown notifications */
  @Prop() numberOfShownNotifications = 3;

  componentWillLoad(): void {
    getNotificationsFlyout();
    state.totalUnreadNotifications = this.badgeCounter;
  }

  @Listen("click", { target: "document" })
  @Listen("keyup", { target: "document" })
  onClickOutside(e: Event): void {
    if (!this.el.contains(e.target as Node)) {
      this.open = false;
    }
  }

  private markAll() {
    const ids = state.notifications
      .map((notification) => !notification.read && notification.code)
      .filter((el) => typeof el === "string");
    markNotification(ids, "read");
  }

  private delete = (code?: string) => {
    deleteNotification(code);
  };

  render(): JSX.Element {
    const notifications = state.notificationsFlyoutList;
    const unread = state.totalUnreadNotifications;
    return (
      <Host data-test="mini-notification">
        <f-flyout-with-button
          flyoutTitle={`${this.header} (${unread ?? 0})`}
          flyoutId="f-notification-flyout"
          buttonIconName="notification-active"
          buttonBadgeLabel={unread}
          buttonTitle={this.buttonTitle}
          closeButtonHiddenLabel={this.closeButtonHiddenLabel}
          flyoutDataTest="notification-flyout"
        >
          <f-visually-hidden>{this.buttonHiddenLabel}</f-visually-hidden>
          <f-flyout-feature-bar slot="flyout-feature-bar" variant="group">
            <span
              slot="feature-bar-group-btn"
              class={"notifications-flyout__feature-bar"}
            >
              <f-button
                type="button"
                variant="integrated"
                linkUrl={this.notificationsSettingsLink}
              >
                <f-icon iconName="settings"></f-icon>
                {this.notificationsSettingsLinkName}
              </f-button>
            </span>
          </f-flyout-feature-bar>
          <div slot="flyout-content">
            {state.is === "LOADED" && notifications.length > 0 && (
              <f-notification-flyout-content-section
                notifications={notifications}
                delete={this.delete}
                timeFormat={this.timeFormat}
                unreadLabel={this.unreadLabel}
                readLabel={this.readLabel}
                removeNotificationLabel={this.removeNotificationLabel}
                numberOfShownNotifications={this.numberOfShownNotifications}
                expanderButtonHiddenLabel={this.expanderButtonHiddenLabel}
              ></f-notification-flyout-content-section>
            )}
            {state.is === "LOADED" && notifications.length === 0 && (
              <f-flyout-message messageLabel={this.emptyFlyoutMessage} />
            )}
            {state.is === "LOADING" && <f-flyout-loader />}
          </div>
          {state.is !== "INIT" && (
            <f-flyout-footer
              slot="flyout-footer"
              mode="secondary"
              link={this.notificationsPageLink}
              label={this.notificationsPageLinkName}
            >
              <div slot="secondary-buttons">
                <f-button variant="integrated" onClick={this.markAll}>
                  <f-icon iconName="mail-open"></f-icon>
                  {this.markAllLabel}
                </f-button>
                <f-button variant="integrated" onClick={() => this.delete()}>
                  <f-icon iconName="delete"></f-icon>
                  {this.deleteAllLabel}
                </f-button>
              </div>
            </f-flyout-footer>
          )}
        </f-flyout-with-button>
      </Host>
    );
  }
}
